import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Dropdown, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function Commandes() {
    const navigate = useNavigate();
    const [commandes, setCommandes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_commande', direction: 'desc' });
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [commandeToDelete, setCommandeToDelete] = useState(null);

    useEffect(() => {
        fetchCommandes();
        fetchStats();
    }, [filtreStatut]);

    const fetchCommandes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/commandes_fournisseurs.php`;
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setCommandes(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (cmd) => {
        setCommandeToDelete(cmd);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!commandeToDelete) return;
        
        setIsDeleting(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php?id=${commandeToDelete.id_commande_fournisseur}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setCommandeToDelete(null);
                fetchCommandes();
                fetchStats();
            } else {
                alert(result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            envoyee: { color: '#3b82f6', icon: 'ri-send-plane-line', label: 'Envoyée' },
            recue_partielle: { color: '#f59e0b', icon: 'ri-stack-line', label: 'Partiellement reçue' },
            recue_complete: { color: '#10b981', icon: 'ri-check-double-line', label: 'Reçue' },
            annulee: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Annulée' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const estEnRetard = (dateLivraison) => {
        return new Date(dateLivraison) < new Date();
    };

    const filteredCommandes = useMemo(() => {
        return commandes
            .filter(c => 
                c.numero_commande?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.fournisseur_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_commande') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_commande) - new Date(b.date_commande)
                        : new Date(b.date_commande) - new Date(a.date_commande);
                }
                if (sortConfig.key === 'montant_ttc') {
                    return sortConfig.direction === 'asc' 
                        ? (a.montant_ttc || 0) - (b.montant_ttc || 0)
                        : (b.montant_ttc || 0) - (a.montant_ttc || 0);
                }
                return 0;
            });
    }, [commandes, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCommandes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCommandes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}>
            <Button 
                variant={variant} 
                size="sm" 
                onClick={onClick} 
                className="action-btn"
            >
                <i className={icon} style={{ fontSize: '1rem' }}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des commandes...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchCommandes}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-shopping-cart-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Commandes fournisseurs</h4>
                                    <p className="text-muted small mb-0">Gérez vos achats et suivez vos commandes</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/achats/commandes/nouveau')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-add-line"></i>
                                Nouvelle commande
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon">
                                        <i className="ri-shopping-cart-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Commandes du mois</span>
                                        <h3 className="kpi-value">{stats.mois?.nb_commandes || 0}</h3>
                                        <span className="kpi-unit">{formatCurrency(stats.mois?.total_montant || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon">
                                        <i className="ri-time-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">En attente</span>
                                        <h3 className="kpi-value">{stats.statuts?.find(s => s.statut === 'envoyee')?.nb || 0}</h3>
                                        <span className="kpi-unit">à recevoir</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon">
                                        <i className="ri-check-double-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Reçues</span>
                                        <h3 className="kpi-value">
                                            {(stats.statuts?.find(s => s.statut === 'recue_complete')?.nb || 0) + 
                                             (stats.statuts?.find(s => s.statut === 'recue_partielle')?.nb || 0)}
                                        </h3>
                                        <span className="kpi-unit">complètes/partielles</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={3}>
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon">
                                        <i className="ri-alert-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">En retard</span>
                                        <h3 className="kpi-value">{stats.retard?.nb || 0}</h3>
                                        <span className="kpi-unit">livraison</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={5}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher commande, fournisseur..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="tous"> Tous les statuts</option>
                                        <option value="brouillon"> Brouillons</option>
                                        <option value="envoyee"> Envoyées</option>
                                        <option value="recue_partielle"> Partiellement reçues</option>
                                        <option value="recue_complete"> Reçues</option>
                                        <option value="annulee"> Annulées</option>
                                    </Form.Select>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredCommandes.length} commande(s)
                                    </span>
                                    <Button
                                        variant="link"
                                        className="text-muted ms-3"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <i className={`ri-equalizer-line fs-5 ${showFilters ? 'text-primary' : ''}`}></i>
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des commandes avec pagination */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° Commande</th>
                                            <SortableHeader field="date_commande">Date</SortableHeader>
                                            <th>Fournisseur</th>
                                            <th>Liv. prévue</th>
                                            <SortableHeader field="montant_ttc">Montant</SortableHeader>
                                            <th>Articles</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((cmd) => {
                                                const retard = estEnRetard(cmd.date_livraison_prevue) && 
                                                              !['recue_complete', 'annulee'].includes(cmd.statut);
                                                
                                                return (
                                                    <tr key={cmd.id_commande_fournisseur} className="commande-row">
                                                        <td className="ps-4">
                                                            <Link to={`/achats/commandes/${cmd.id_commande_fournisseur}`} className="table-link">
                                                                {cmd.numero_commande}
                                                            </Link>
                                                       </td>
                                                        <td>
                                                            <small className="text-muted">
                                                                <i className="ri-calendar-line me-1 fs-10"></i>
                                                                {formatDate(cmd.date_commande)}
                                                            </small>
                                                     </td>
                                                        <td>
                                                            <Link to={`/achats/fournisseurs/${cmd.id_fournisseur}`} className="table-link">
                                                                {cmd.fournisseur_nom}
                                                            </Link>
                                                            <div><small className="text-muted">{cmd.code_fournisseur}</small></div>
                                                  </td>
                                                        <td>
                                                            <span className={retard ? 'text-danger fw-bold' : ''}>
                                                                {cmd.date_livraison_prevue ? formatDate(cmd.date_livraison_prevue) : '-'}
                                                                {retard && <Badge bg="danger" className="ms-2 retard-badge">En retard</Badge>}
                                                            </span>
                                                  </td>
                                                        <td className="fw-bold text-primary">{formatCurrency(cmd.montant_ttc || 0)}</td>
                                                        <td>
                                                            <Badge bg="light" className="px-2 py-1 me-1" style={{ background: '#e2f3f5', color: '#21ACCD' }}>
                                                                {cmd.nb_articles || 0} art.
                                                            </Badge>
                                                            <small className="text-muted d-block">
                                                                Qté: {cmd.total_quantite || 0}
                                                            </small>
                                                       </td>
                                                        <td>{getStatutBadge(cmd.statut)}</td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/achats/commandes/${cmd.id_commande_fournisseur}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {cmd.statut === 'brouillon' && (
                                                                    <>
                                                                        <ActionButton
                                                                            variant="outline-primary"
                                                                            icon="ri-pencil-line"
                                                                            onClick={() => navigate(`/achats/commandesModifier/${cmd.id_commande_fournisseur}`)}
                                                                            tooltip="Modifier"
                                                                        />
                                                                        <ActionButton
                                                                            variant="outline-danger"
                                                                            icon="ri-delete-bin-line"
                                                                            onClick={() => handleDeleteClick(cmd)}
                                                                            tooltip="Supprimer"
                                                                        />
                                                                    </>
                                                                )}
                                                            </div>
                                                      </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-shopping-cart-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucune commande trouvée</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par créer votre première commande"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/achats/commandes/nouveau')}
                                                                className="rounded-pill"
                                                                style={{ background: '#21ACCD', border: 'none' }}
                                                            >
                                                                <i className="ri-add-line me-2"></i>Nouvelle commande
                                                            </Button>
                                                        )}
                                                    </div>
</td>
                                             </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredCommandes.length)} sur {filteredCommandes.length} commandes
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal de suppression modernisé */}
                <Modal 
                    show={showDeleteModal} 
                    onHide={() => setShowDeleteModal(false)} 
                    centered 
                    className="modern-modal delete-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Supprimer la commande</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir supprimer la commande <strong className="text-primary">{commandeToDelete?.numero_commande}</strong> ?
                        </p>
                        <Alert variant="warning" className="rounded-3 text-start">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        La suppression entraînera la perte définitive de toutes les informations 
                                        liées à cette commande. Cette action ne peut pas être annulée.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                        <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Fournisseur :</span>
                                <span className="fw-semibold">{commandeToDelete?.fournisseur_nom}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Montant :</span>
                                <span className="fw-semibold text-primary">{formatCurrency(commandeToDelete?.montant_ttc || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">Date commande :</span>
                                <span className="fw-semibold">{formatDate(commandeToDelete?.date_commande)}</span>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDeleteModal(false)}
                            className="px-4 rounded-pill"
                            disabled={isDeleting}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleConfirmDelete}
                            className="px-5 rounded-pill"
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Supprimer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .commande-row {
                    transition: all 0.2s ease;
                }
                
                .commande-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                /* Retard Badge */
                .retard-badge {
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}